import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { motion } from 'framer-motion';
import { Users, Play, CheckCircle, AlertCircle, Map, X } from 'lucide-react';
import {
  PlotPreference,
  PlotAssignment,
  getAllPlotPreferences,
  savePlotAssignments,
  getActiveAssignments,
  unassignUserFromPlot,
} from '../services/housingService';
import { assignHousingPlots } from '../utils/housingAssignmentAlgorithm';

// Housing areas and their available plots
const HOUSING_DATA = {
  'Cliffside': ['31', '23', '32', '03', '22'],
  'Brumewood Hallow': ['0', '17', '06', '21', '38', '08', '33', '20', '18', '41', '10', '02', '15'],
  'Gilded Oaks': ['13', '30', '01', '35', '26', '37', '05', '14', '36'],
  'The Outer Banks': ['24', '43', '28', '29', '52', '19', '50', '40', '25', '12', '04'],
  'Mear\'s Farm': ['42', '39', '45', '34'],
  'Central': ['48', '53', '44', '27', '07', '09', '47', '51', '49', '46', '54', '16', '11'],
};

const HousingPlotAdmin: React.FC = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  
  const [activeTab, setActiveTab] = useState<'view' | 'assign'>('view');
  const [preferences, setPreferences] = useState<PlotPreference[]>([]);
  const [assignments, setAssignments] = useState<PlotAssignment[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Manual assignment state
  const [selectedUsername, setSelectedUsername] = useState<string>('');
  const [selectedArea, setSelectedArea] = useState<string>('');
  const [selectedPlotNumber, setSelectedPlotNumber] = useState<string>('');
  
  
  // Admin check
  useEffect(() => {
    if (authLoading) return;
    if (!user || user.role !== 'admin') {
      navigate('/');
      return;
    }
    
    // Load existing preferences
    loadPreferences();
    
    // Load active assignments if they exist
    loadActiveAssignments();
  }, [authLoading, user, navigate]);
  
  // Reload assignments when switching to view tab to ensure we have latest data
  useEffect(() => {
    if (activeTab === 'view' && user && user.role === 'admin') {
      loadActiveAssignments();
    }
  }, [activeTab, user]);
  
  const loadPreferences = async () => {
    setLoading(true);
    const prefs = await getAllPlotPreferences();
    setPreferences(prefs);
    setLoading(false);
  };
  
  
  const loadActiveAssignments = async () => {
    try {
      const activeAssignments = await getActiveAssignments();
      setAssignments(activeAssignments); // Always set, even if empty
      console.log(`Loaded ${activeAssignments.length} active assignments`);
    } catch (error) {
      console.error('Error loading active assignments:', error);
      setAssignments([]); // Set empty array on error
    }
  };
  
  // Refresh both preferences and assignments
  const refreshData = async () => {
    await Promise.all([loadPreferences(), loadActiveAssignments()]);
  };
  
  const handleRunAssignment = async () => {
    setError(null);
    setSuccess(null);
    setLoading(true);
    
    try {
      // Run the assignment algorithm with housing data
      const result = assignHousingPlots(preferences, HOUSING_DATA);
      setAssignments(result);
      
      // Save the assignments
      const saveResult = await savePlotAssignments(result);
      if (saveResult) {
        setError(saveResult);
      } else {
        setSuccess('Assignments completed and saved!');
        await loadPreferences(); // Reload to show updated assignments
      }
    } catch (err: any) {
      setError(err.message || 'Failed to run assignment algorithm');
    }
    
    setLoading(false);
  };

  const handleManualSingleAssignment = async (username: string, plotArea: string, plotNumber: string) => {
    setError(null);
    setSuccess(null);
    setLoading(true);
    
    try {
      // Load current assignments
      const currentAssignments = await getActiveAssignments();
      
      // Find the user's preference
      const userPref = preferences.find(p => 
        p.username.toLowerCase() === username.toLowerCase() ||
        p.characterName?.toLowerCase() === username.toLowerCase()
      );
      
      if (!userPref) {
        setError(`User "${username}" not found in preferences. Please ensure they have submitted a preference first.`);
        setLoading(false);
        return;
      }
      
      // Helper function to calculate preference rank
      const calculatePreferenceRankForPlot = (pref: PlotPreference, plotFullName: string): number => {
        const topIndex = pref.topChoices.indexOf(plotFullName);
        if (topIndex !== -1) return topIndex + 1;
        
        const bottomIndex = pref.bottomChoices.indexOf(plotFullName);
        if (bottomIndex !== -1) return -(bottomIndex + 1);
        
        // Check by area
        const plotMatch = plotFullName.match(/^(.+) - Plot/);
        if (plotMatch) {
          const plotArea = plotMatch[1];
          for (let i = 0; i < pref.topChoices.length; i++) {
            const choiceMatch = pref.topChoices[i].match(/^(.+) - Plot/);
            if (choiceMatch && choiceMatch[1] === plotArea) return i + 1;
          }
          for (let i = 0; i < pref.bottomChoices.length; i++) {
            const choiceMatch = pref.bottomChoices[i].match(/^(.+) - Plot/);
            if (choiceMatch && choiceMatch[1] === plotArea) return -(i + 1);
          }
        }
        
        return 0; // Neutral
      };
      
      const plotFullName = `${plotArea} - Plot ${plotNumber}`;
      const preferenceRank = calculatePreferenceRankForPlot(userPref, plotFullName);
      
      // Remove any existing assignment for this user
      const updatedAssignments = currentAssignments.filter(a => a.userId !== userPref.userId);
      
      // Remove assignment from the plot if it's already assigned to someone else
      const plotAlreadyAssigned = updatedAssignments.find(a => a.assignedPlot === plotFullName);
      if (plotAlreadyAssigned) {
        const withoutPlotAssignment = updatedAssignments.filter(a => a.assignedPlot !== plotFullName);
        updatedAssignments.length = 0;
        updatedAssignments.push(...withoutPlotAssignment);
      }
      
      // Add new assignment
      updatedAssignments.push({
        userId: userPref.userId,
        username: userPref.username,
        characterName: userPref.characterName,
        assignedPlot: plotFullName,
        preferenceRank: preferenceRank,
        satisfiedNeighborRequest: false, // Could check neighbor requests if needed
        needsManualReview: false,
        assignedAt: new Date(),
      });
      
      // Save updated assignments
      const saveResult = await savePlotAssignments(updatedAssignments);
      if (saveResult) {
        setError(saveResult);
      } else {
        setSuccess(`Successfully assigned ${userPref.username} to ${plotFullName}`);
        setAssignments(updatedAssignments);
        // Reset form
        setSelectedUsername('');
        setSelectedArea('');
        setSelectedPlotNumber('');
        await loadPreferences(); // Reload to show updated assignments
        await loadActiveAssignments(); // Reload assignments
      }
    } catch (err: any) {
      setError(err.message || 'Failed to save manual assignment');
    }
    
    setLoading(false);
  };

  const handleUnassignUser = async (userId: string, username: string) => {
    if (!confirm(`Are you sure you want to unassign ${username} from their housing plot?`)) {
      return;
    }

    setError(null);
    setSuccess(null);
    setLoading(true);

    try {
      const result = await unassignUserFromPlot(userId);
      if (result) {
        setError(result);
      } else {
        setSuccess(`Successfully unassigned ${username} from their plot`);
        // Reload data to reflect changes
        await loadPreferences();
        await loadActiveAssignments();
      }
    } catch (err: any) {
      setError(err.message || 'Failed to unassign user');
    }

    setLoading(false);
  };

  const exportToCSV = (data: 'submissions' | 'assignments') => {
    let csvContent = '';
    let filename = '';
    
    if (data === 'submissions') {
      // Export submissions data with assigned plots if available
      filename = `housing_submissions_${new Date().toISOString().split('T')[0]}.csv`;
      csvContent = 'Username,Priority Level,Top Choice 1,Top Choice 2,Top Choice 3,Bottom Choice 1,Bottom Choice 2,Bottom Choice 3,Preferred Neighbor,Submission Date,Assigned Plot,Preference Rank,Neighbor Match Satisfied\n';
      
      preferences.forEach(pref => {
        // Find corresponding assignment if it exists
        const assignment = assignments.find(a => a.userId === pref.userId);
        
        let rankDescription = '';
        if (assignment) {
          if (assignment.satisfiedNeighborRequest) {
            rankDescription = 'Neighbor Match';
          } else if (assignment.preferenceRank > 0) {
            rankDescription = `Choice #${assignment.preferenceRank}`;
          } else if (assignment.preferenceRank < 0) {
            rankDescription = `Bottom Choice #${Math.abs(assignment.preferenceRank)}`;
          } else {
            rankDescription = 'Neutral';
          }
        }
        
        const row = [
          pref.username,
          pref.priorityLevel || 3,
          pref.topChoices[0] || '',
          pref.topChoices[1] || '',
          pref.topChoices[2] || '',
          pref.bottomChoices[0] || '',
          pref.bottomChoices[1] || '',
          pref.bottomChoices[2] || '',
          pref.preferredNeighbor || '',
          new Date(pref.submittedAt?.toDate?.() || pref.submittedAt).toLocaleDateString(),
          assignment?.assignedPlot || '',
          rankDescription || '',
          assignment?.satisfiedNeighborRequest ? 'Yes' : 'No'
        ];
        csvContent += row.map(cell => `"${cell}"`).join(',') + '\n';
      });
    } else {
      // Export assignments data
      filename = `housing_assignments_${new Date().toISOString().split('T')[0]}.csv`;
      csvContent = 'Username,Assigned Plot,Preference Rank,Rank Description,Neighbor Match Satisfied,Needs Manual Review\n';
      
      assignments.forEach(assignment => {
        let rankDescription = '';
        if (assignment.satisfiedNeighborRequest) {
          rankDescription = 'Neighbor Match';
        } else if (assignment.preferenceRank > 0) {
          rankDescription = `Choice #${assignment.preferenceRank}`;
        } else if (assignment.preferenceRank < 0) {
          rankDescription = `Bottom Choice #${Math.abs(assignment.preferenceRank)}`;
        } else {
          rankDescription = 'Neutral';
        }
        
        const row = [
          assignment.username,
          assignment.assignedPlot,
          assignment.preferenceRank.toString(),
          rankDescription,
          assignment.satisfiedNeighborRequest ? 'Yes' : 'No',
          assignment.needsManualReview ? 'Yes' : 'No'
        ];
        csvContent += row.map(cell => `"${cell}"`).join(',') + '\n';
      });
    }
    
    // Create download link
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  if (authLoading) {
    return (
      <div className="page-container bg-surface flex items-center justify-center">
        <p className="text-text-secondary">Loading...</p>
      </div>
    );
  }
  
  return (
    <div className="page-container bg-surface">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            <span className="gradient-text">
              Housing Plot Assignment
            </span>
          </h1>

        </motion.div>
        
        {/* Tabs */}
        <div className="flex gap-4 mb-8 justify-center flex-wrap">
          <button
            onClick={() => setActiveTab('view')}
            className={`px-6 py-3 rounded-lg font-semibold transition-all ${
              activeTab === 'view'
                ? 'bg-gradient-to-r from-primary to-secondary text-white'
                : 'bg-surface-light text-text-secondary hover:text-text-primary'
            }`}
          >
            View Submissions ({preferences.length})
          </button>
          <button
            onClick={() => setActiveTab('assign')}
            className={`px-6 py-3 rounded-lg font-semibold transition-all ${
              activeTab === 'assign'
                ? 'bg-gradient-to-r from-primary to-secondary text-white'
                : 'bg-surface-light text-text-secondary hover:text-text-primary'
            }`}
          >
            Run Assignment
          </button>
          <Link
            to="/housing-map"
            className="px-6 py-3 rounded-lg font-semibold transition-all bg-gradient-to-r from-primary to-secondary text-white hover:opacity-90 flex items-center gap-2"
          >
            <Map size={20} />
            Housing Map
          </Link>
        </div>
        
        {/* Error/Success Messages */}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-red-500/20 border border-red-500/50 rounded-lg p-4 mb-6 flex items-center gap-3"
          >
            <AlertCircle className="text-red-400" size={20} />
            <p className="text-red-400">{error}</p>
          </motion.div>
        )}
        
        {success && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-green-500/20 border border-green-500/50 rounded-lg p-4 mb-6 flex items-center gap-3"
          >
            <CheckCircle className="text-green-400" size={20} />
            <p className="text-green-400">{success}</p>
          </motion.div>
        )}
        
        {/* Tab Content */}
        {activeTab === 'view' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-4"
          >
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold flex items-center gap-2">
                <Users className="text-primary" />
                All Users & Assignments ({Math.max(preferences.length, assignments.length)})
              </h2>
              
              <div className="flex gap-2">
                <button
                  onClick={refreshData}
                  disabled={loading}
                  className="px-4 py-2 bg-surface-light border border-border rounded-lg font-semibold hover:bg-surface text-text-primary disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2"
                  title="Refresh data"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Refresh
                </button>
                {preferences.length > 0 && (
                  <button
                    onClick={() => exportToCSV('submissions')}
                    className="px-4 py-2 bg-gradient-to-r from-primary to-secondary text-white rounded-lg font-semibold hover:opacity-90 transition-opacity flex items-center gap-2"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Export to CSV
                  </button>
                )}
              </div>
            </div>
            
            {preferences.length === 0 && assignments.length === 0 ? (
              <div className="glass p-12 rounded-xl text-center">
                <p className="text-text-secondary text-lg">No submissions or assignments yet</p>
              </div>
            ) : (() => {
              // Create a merged list: start with all preferences, then add assignments without preferences
              const preferenceUserIds = new Set(preferences.map(p => p.userId));
              const assignmentsWithoutPreferences = assignments.filter(a => !preferenceUserIds.has(a.userId));
              
              // Combine: preferences (with merged assignment data) + assignments without preferences
              const allUsers = [
                ...preferences.map(pref => {
                  const assignment = assignments.find(a => a.userId === pref.userId);
                  return {
                    type: 'preference' as const,
                    id: pref.id,
                    userId: pref.userId,
                    username: pref.username,
                    characterName: pref.characterName,
                    assignedPlot: assignment?.assignedPlot || pref.assignedPlot,
                    preferenceRank: assignment?.preferenceRank ?? pref.preferenceRank,
                    satisfiedNeighborRequest: assignment?.satisfiedNeighborRequest ?? pref.satisfiedNeighborRequest,
                    needsManualReview: assignment?.needsManualReview ?? pref.needsManualReview,
                    priorityLevel: pref.priorityLevel,
                    topChoices: pref.topChoices,
                    bottomChoices: pref.bottomChoices,
                    preferredNeighbor: pref.preferredNeighbor,
                    submittedAt: pref.submittedAt,
                  };
                }),
                ...assignmentsWithoutPreferences.map(assignment => ({
                  type: 'assignment-only' as const,
                  id: `assignment-${assignment.userId}`,
                  userId: assignment.userId,
                  username: assignment.username,
                  characterName: assignment.characterName,
                  assignedPlot: assignment.assignedPlot,
                  preferenceRank: assignment.preferenceRank,
                  satisfiedNeighborRequest: assignment.satisfiedNeighborRequest,
                  needsManualReview: assignment.needsManualReview,
                  priorityLevel: undefined,
                  topChoices: [] as string[],
                  bottomChoices: [] as string[],
                  preferredNeighbor: undefined,
                  submittedAt: undefined,
                }))
              ];
              
              return allUsers.map((user) => (
                <div key={user.id} className="glass p-6 rounded-xl">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-xl font-bold text-text-primary">
                        {user.username}
                        {user.characterName && (
                          <span className="text-sm text-text-secondary ml-2">({user.characterName})</span>
                        )}
                      </h3>
                      {user.submittedAt && (
                        <p className="text-sm text-text-secondary">
                          Submitted: {new Date(user.submittedAt?.toDate?.() || user.submittedAt).toLocaleDateString()}
                        </p>
                      )}
                      {user.type === 'assignment-only' && (
                        <p className="text-sm text-yellow-400 italic">
                          No preference submitted (selected plot directly)
                        </p>
                      )}
                    </div>
                    {user.assignedPlot && (
                      <div className="flex flex-col items-end gap-2">
                        <div className="flex items-center gap-2">
                          <div className="bg-green-500/20 border border-green-500/50 rounded-lg px-4 py-2">
                            <p className="text-green-400 font-semibold">
                              Assigned: {user.assignedPlot}
                            </p>
                          </div>
                          <button
                            onClick={() => handleUnassignUser(user.userId, user.username)}
                            disabled={loading}
                            className="px-3 py-2 bg-red-500/20 border border-red-500/50 rounded-lg text-red-400 hover:bg-red-500/30 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-1"
                            title="Unassign user from plot"
                          >
                            <X size={16} />
                            Unassign
                          </button>
                        </div>
                        <div className="flex gap-2">
                          {user.satisfiedNeighborRequest && (
                            <span className="px-3 py-1 bg-blue-500/20 border border-blue-500/50 rounded text-blue-400 text-sm">
                              Neighbor Match
                            </span>
                          )}
                          {user.preferenceRank !== undefined && user.preferenceRank > 0 && (
                            <span className="px-3 py-1 bg-green-500/20 border border-green-500/50 rounded text-green-400 text-sm">
                              Choice #{user.preferenceRank}
                            </span>
                          )}
                          {user.preferenceRank !== undefined && user.preferenceRank < 0 && (
                            <span className="px-3 py-1 bg-red-500/20 border border-red-500/50 rounded text-red-400 text-sm">
                              Bottom Choice
                            </span>
                          )}
                          {user.preferenceRank !== undefined && user.preferenceRank === 0 && (
                            <span className="px-3 py-1 bg-yellow-500/20 border border-yellow-500/50 rounded text-yellow-400 text-sm">
                              Neutral
                            </span>
                          )}
                          {user.needsManualReview && (
                            <span className="px-3 py-1 bg-red-500/20 border border-red-500/50 rounded text-red-300 text-sm">
                              Needs Admin Review
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {user.priorityLevel !== undefined && (
                    <div className="mb-3">
                      <span className={`inline-block px-3 py-1 rounded-full text-sm font-semibold ${
                        user.priorityLevel === 5 ? 'bg-green-500/20 text-green-400 border border-green-500/50' :
                        user.priorityLevel === 4 ? 'bg-lime-500/20 text-lime-400 border border-lime-500/50' :
                        user.priorityLevel === 3 ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/50' :
                        user.priorityLevel === 2 ? 'bg-orange-500/20 text-orange-400 border border-orange-500/50' :
                        'bg-red-500/20 text-red-400 border border-red-500/50'
                      }`}>
                        Priority: {user.priorityLevel}/5
                      </span>
                    </div>
                  )}
                  
                  {user.type === 'preference' && (user.topChoices.length > 0 || user.bottomChoices.length > 0) && (
                    <div className="grid md:grid-cols-2 gap-4 mb-4">
                      {user.topChoices.length > 0 && (
                        <div>
                          <p className="text-sm font-medium text-text-secondary mb-2">Top Choices:</p>
                          <ol className="space-y-1">
                            {user.topChoices.map((choice, idx) => {
                              const match = choice.match(/^(.+) - Plot (.+)$/);
                              if (match) {
                                return (
                                  <li key={idx} className="text-green-400">
                                    {idx + 1}. <span className="font-semibold">{match[1]}</span> - Plot {match[2]}
                                  </li>
                                );
                              }
                              return (
                                <li key={idx} className="text-green-400">
                                  {idx + 1}. {choice}
                                </li>
                              );
                            })}
                          </ol>
                        </div>
                      )}
                      {user.bottomChoices.length > 0 && (
                        <div>
                          <p className="text-sm font-medium text-text-secondary mb-2">Bottom Choices:</p>
                          <ul className="space-y-1">
                            {user.bottomChoices.map((choice, idx) => {
                              const match = choice.match(/^(.+) - Plot (.+)$/);
                              if (match) {
                                return (
                                  <li key={idx} className="text-red-400">
                                    • <span className="font-semibold">{match[1]}</span> - Plot {match[2]}
                                  </li>
                                );
                              }
                              return (
                                <li key={idx} className="text-red-400">
                                  • {choice}
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {user.preferredNeighbor && (
                    <div className="bg-blue-500/20 border border-blue-500/50 rounded-lg p-3">
                      <p className="text-blue-400">
                        <Users size={16} className="inline mr-2" />
                        Preferred Neighbor: <strong>{user.preferredNeighbor}</strong>
                      </p>
                    </div>
                  )}
                </div>
              ));
            })()}
          </motion.div>
        )}
        
        {activeTab === 'assign' && (
          <>
            {/* Manual Assignment */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="glass p-8 rounded-xl max-w-4xl mx-auto mb-8"
            >
              <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
                <CheckCircle className="text-primary" />
                Manual Plot Assignment
              </h2>
              
              <div className="bg-blue-500/20 border border-blue-500/50 rounded-lg p-4 mb-6">
                <p className="text-blue-400 text-sm">
                  <strong>Note:</strong> Use this to manually assign plots to late submissions or make adjustments. This will update the current assignments.
                </p>
              </div>
              
              <div className="space-y-4">
                {/* Username Selection */}
                <div>
                  <label className="block text-sm font-semibold text-text-primary mb-2">
                    Select User
                  </label>
                  <select
                    value={selectedUsername}
                    onChange={(e) => setSelectedUsername(e.target.value)}
                    className="w-full px-4 py-2 bg-surface-light border border-border rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="">-- Select a user --</option>
                    {preferences
                      .filter(pref => {
                        // Only show users who don't have an assignment yet, or show all for reassignment
                        const hasAssignment = assignments.find(a => a.userId === pref.userId);
                        return !hasAssignment || true; // Show all for now, can filter if needed
                      })
                      .map(pref => (
                        <option key={pref.userId} value={pref.username}>
                          {pref.username}{pref.characterName ? ` (${pref.characterName})` : ''}
                        </option>
                      ))}
                  </select>
                </div>
                
                {/* Area Selection */}
                <div>
                  <label className="block text-sm font-semibold text-text-primary mb-2">
                    Select Area
                  </label>
                  <select
                    value={selectedArea}
                    onChange={(e) => {
                      setSelectedArea(e.target.value);
                      setSelectedPlotNumber(''); // Reset plot number when area changes
                    }}
                    className="w-full px-4 py-2 bg-surface-light border border-border rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="">-- Select an area --</option>
                    {Object.keys(HOUSING_DATA).map(area => (
                      <option key={area} value={area}>
                        {area}
                      </option>
                    ))}
                  </select>
                </div>
                
                {/* Plot Number Selection */}
                {selectedArea && (
                  <div>
                    <label className="block text-sm font-semibold text-text-primary mb-2">
                      Select Plot Number
                    </label>
                    <select
                      value={selectedPlotNumber}
                      onChange={(e) => setSelectedPlotNumber(e.target.value)}
                      className="w-full px-4 py-2 bg-surface-light border border-border rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-primary"
                    >
                      <option value="">-- Select a plot --</option>
                      {HOUSING_DATA[selectedArea as keyof typeof HOUSING_DATA]?.map(plotNum => {
                        const plotFullName = `${selectedArea} - Plot ${plotNum}`;
                        const isAssigned = assignments.some(a => a.assignedPlot === plotFullName);
                        const assignedTo = assignments.find(a => a.assignedPlot === plotFullName);
                        return (
                          <option 
                            key={plotNum} 
                            value={plotNum}
                            disabled={isAssigned && assignedTo?.username !== selectedUsername}
                          >
                            Plot {plotNum}{isAssigned ? ` (Currently: ${assignedTo?.username})` : ' (Available)'}
                          </option>
                        );
                      })}
                    </select>
                    {selectedPlotNumber && assignments.some(a => {
                      const plotFullName = `${selectedArea} - Plot ${selectedPlotNumber}`;
                      return a.assignedPlot === plotFullName && a.username !== selectedUsername;
                    }) && (
                      <p className="text-yellow-400 text-sm mt-2">
                        ⚠️ This plot is currently assigned to someone else. They will be unassigned.
                      </p>
                    )}
                  </div>
                )}
                
                {/* Assign Button */}
                <button
                  onClick={() => {
                    if (selectedUsername && selectedArea && selectedPlotNumber) {
                      handleManualSingleAssignment(selectedUsername, selectedArea, selectedPlotNumber);
                    }
                  }}
                  disabled={loading || !selectedUsername || !selectedArea || !selectedPlotNumber || preferences.length === 0}
                  className="w-full px-6 py-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg font-semibold hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity flex items-center justify-center gap-2"
                >
                  <CheckCircle size={20} />
                  {loading 
                    ? 'Assigning...' 
                    : selectedUsername && selectedArea && selectedPlotNumber
                      ? `Assign ${selectedUsername} to ${selectedArea} - Plot ${selectedPlotNumber}`
                      : 'Select user, area, and plot to assign'}
                </button>
              </div>
            </motion.div>
            
            {/* Run Assignment Algorithm */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="glass p-8 rounded-xl max-w-4xl mx-auto"
            >
              <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
                <Play className="text-primary" />
                Run Assignment Algorithm
              </h2>
            
            <div className="space-y-6">
              <div className="bg-blue-500/20 border border-blue-500/50 rounded-lg p-6">
                <h3 className="font-semibold text-blue-400 mb-3">Algorithm Priority Order:</h3>
                <ol className="space-y-2 text-text-secondary">
                  <li>1. <strong className="text-text-primary">Neighbor Matching</strong> - Highest priority</li>
                  <li>2. <strong className="text-text-primary">Top Preferences</strong> - Try to match 1st, 2nd, or 3rd choice</li>
                  <li>3. <strong className="text-text-primary">Avoid Bottom Choices</strong> - Unless needed for neighbor matching</li>
                </ol>
              </div>
              
              <button
                onClick={handleRunAssignment}
                disabled={loading || preferences.length === 0}
                className="w-full px-6 py-4 bg-gradient-to-r from-primary to-secondary text-white rounded-lg font-semibold hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity flex items-center justify-center gap-2"
              >
                <Play size={20} />
                {loading 
                  ? 'Running Assignment...' 
                  : assignments.length > 0 
                    ? 'Re-run Assignment Algorithm (Overwrites Current)' 
                    : 'Run Assignment Algorithm'}
              </button>
              
              {assignments.length > 0 && (
                <div className="bg-yellow-500/20 border border-yellow-500/50 rounded-lg p-3 text-sm text-yellow-400">
                  ⚠️ Assignments have already been run. Clicking the button above will overwrite them.
                </div>
              )}
              
              {/* Results */}
              {assignments.length > 0 && (
                <div className="mt-8 space-y-4">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-xl font-bold text-text-primary">Assignment Results:</h3>
                    <button
                      onClick={() => exportToCSV('assignments')}
                      className="px-4 py-2 bg-gradient-to-r from-primary to-secondary text-white rounded-lg font-semibold hover:opacity-90 transition-opacity flex items-center gap-2"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      Export Results to CSV
                    </button>
                  </div>
                  
                  {/* Statistics */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    <div className="bg-surface-light rounded-lg p-4 text-center">
                      <p className="text-2xl font-bold text-green-400">
                        {assignments.filter(a => a.preferenceRank >= 1 && a.preferenceRank <= 3).length}
                      </p>
                      <p className="text-sm text-text-secondary">Got Top Choice</p>
                    </div>
                    <div className="bg-surface-light rounded-lg p-4 text-center">
                      <p className="text-2xl font-bold text-yellow-400">
                        {assignments.filter(a => a.preferenceRank === 0).length}
                      </p>
                      <p className="text-sm text-text-secondary">Neutral</p>
                    </div>
                    <div className="bg-surface-light rounded-lg p-4 text-center">
                      <p className="text-2xl font-bold text-red-400">
                        {assignments.filter(a => a.preferenceRank < 0).length}
                      </p>
                      <p className="text-sm text-text-secondary">Got Bottom Choice</p>
                    </div>
                    <div className="bg-surface-light rounded-lg p-4 text-center">
                      <p className="text-2xl font-bold text-blue-400">
                        {assignments.filter(a => a.satisfiedNeighborRequest).length}
                      </p>
                      <p className="text-sm text-text-secondary">Neighbor Match</p>
                    </div>
                  </div>
                  
                  {/* Individual Results */}
                  {assignments.map((assignment, idx) => (
                    <div key={idx} className="bg-surface-light rounded-lg p-4">
                      <div className="flex justify-between items-center">
                        <div>
                          <p className="font-semibold text-text-primary">
                            {assignment.username}
                          </p>
                          <p className="text-sm text-text-secondary">{assignment.assignedPlot}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="flex gap-2">
                            {assignment.satisfiedNeighborRequest && (
                              <span className="px-3 py-1 bg-blue-500/20 border border-blue-500/50 rounded text-blue-400 text-sm">
                                Neighbor Match
                              </span>
                            )}
                            {assignment.preferenceRank > 0 && (
                              <span className="px-3 py-1 bg-green-500/20 border border-green-500/50 rounded text-green-400 text-sm">
                                Choice #{assignment.preferenceRank}
                              </span>
                            )}
                            {assignment.preferenceRank < 0 && (
                              <span className="px-3 py-1 bg-red-500/20 border border-red-500/50 rounded text-red-400 text-sm">
                                Bottom Choice
                              </span>
                            )}
                            {assignment.preferenceRank === 0 && (
                              <span className="px-3 py-1 bg-yellow-500/20 border border-yellow-500/50 rounded text-yellow-400 text-sm">
                                Neutral
                              </span>
                            )}
                            {assignment.needsManualReview && (
                              <span className="px-3 py-1 bg-red-500/20 border border-red-500/50 rounded text-red-300 text-sm">
                                Needs Admin Review
                              </span>
                            )}
                          </div>
                          <button
                            onClick={() => handleUnassignUser(assignment.userId, assignment.username)}
                            disabled={loading}
                            className="px-3 py-1 bg-red-500/20 border border-red-500/50 rounded text-red-400 hover:bg-red-500/30 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-1 text-sm"
                            title="Unassign user from plot"
                          >
                            <X size={14} />
                            Unassign
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
          </>
        )}
      </div>
      
    </div>
  );
};

export default HousingPlotAdmin;

